import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarRef,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { ConfigurationService } from '@sc/data-access-configuration';
import { levelLoggerCompare, LoggerLevelType } from '@sc/data-access-models';
import { FocusManagerService } from '@sc/feature-focus-manager';
import { take } from 'rxjs/operators';
import { SnackbarComponent } from '../components';
import { SnackbarBottomComponent } from '../components/snackbar-bottom/snackbar-bottom.component';
import { SnackbarErrorComponent } from '../components/snackbar-error/snackbar-error.component';
import { SnackbarNotificationComponent } from '../components/snackbar-notification/snackbar-notification.component';
import { SnackbarSuccessComponent } from '../components/snackbar-success/snackbar-success.component';
import { SnackBarData } from '../types';

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  private focusManagementActive = false;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly configurationService: ConfigurationService,
    private readonly focusManager: FocusManagerService
  ) {}

  setFocusManagement(
    activate: boolean,
    initOptions?: { form?: FormGroup; documentQuery?: string }
  ) {
    this.focusManagementActive = activate;

    if (initOptions?.form) {
      this.focusManager.setFocusForm(initOptions.form);
    }

    if (initOptions?.documentQuery) {
      this.focusManager.setFocusQuery(initOptions.documentQuery);
    }
  }

  setFocusManagementForm(form?: FormGroup) {
    this.focusManager.setFocusForm(form);
  }

  setFocusManagementQuery(queryDoc?: string) {
    this.focusManager.setFocusQuery(queryDoc);
  }

  private applyFocusOnSnackbar(
    snackbarRef: MatSnackBarRef<unknown>,
    snackbarClass: string
  ) {
    if (this.focusManagementActive) {
      snackbarRef
        .afterOpened()
        .pipe(take(1))
        .subscribe(() => {
          this.focusManager.focusFirstElementAllowed(
            `.mat-snack-bar-container.${snackbarClass}`
          );
        });

      snackbarRef
        .afterDismissed()
        .pipe(take(1))
        .subscribe(() => {
          this.focusManager.applyFocus();
        });
    }
  }

  show(
    data: SnackBarData,
    durationInSecond: number = 0,
    type: LoggerLevelType = LoggerLevelType.INFO
  ): void {
    if (
      levelLoggerCompare(
        type,
        this.configurationService.getConfig().loggerLevel
      )
    ) {
      const snackbarClass = 'snackbar-class';

      const snackbarRef = this.snackBar.openFromComponent(SnackbarComponent, {
        duration: durationInSecond * 1000,
        data: {
          ...data,
          currentSnackBar: this.snackBar,
        },
        panelClass: [snackbarClass],
      });

      this.applyFocusOnSnackbar(snackbarRef, snackbarClass);
    }
  }

  showSuccess(
    message: string,
    durationInSecond: number = 0,
    horizontalPosition?: MatSnackBarHorizontalPosition,
    verticalPosition?: MatSnackBarVerticalPosition,
    type: LoggerLevelType = LoggerLevelType.SUCCESS
  ): void {
    if (
      levelLoggerCompare(
        type,
        this.configurationService.getConfig().loggerLevel
      )
    ) {
      const snackbarClass = 'snackbar-success-class';

      const snackbarRef = this.snackBar.openFromComponent(
        SnackbarSuccessComponent,
        {
          duration: durationInSecond * 1000,
          horizontalPosition: horizontalPosition
            ? horizontalPosition
            : 'center',
          verticalPosition: verticalPosition ? verticalPosition : 'bottom',
          data: {
            message: message,
            currentSnackBar: this.snackBar,
          },
          panelClass: [snackbarClass],
        }
      );

      this.applyFocusOnSnackbar(snackbarRef, snackbarClass);
    }
  }

  showError(
    title: string,
    message: string,
    durationInSecond: number = 0,
    type: LoggerLevelType = LoggerLevelType.ERROR
  ): void {
    if (
      levelLoggerCompare(
        type,
        this.configurationService.getConfig().loggerLevel
      ) &&
      !this.isSnackBarAlreadyOpened()
    ) {
      const snackbarClass = 'snackbar-error-class';

      const snackbarRef = this.snackBar.openFromComponent(
        SnackbarErrorComponent,
        {
          duration: durationInSecond * 1000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          data: {
            title: title,
            message: message,
            currentSnackBar: this.snackBar,
          },
          panelClass: [snackbarClass],
        }
      );

      this.applyFocusOnSnackbar(snackbarRef, snackbarClass);
    }
  }

  showNotification(
    title: string,
    message: string,
    durationInSecond: number = 0
  ): void {
    const snackbarClass = 'snackbar-notification-class';

    const snackbarRef = this.snackBar.openFromComponent(
      SnackbarNotificationComponent,
      {
        duration: durationInSecond * 1000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        data: {
          title: title,
          message: message,
          currentSnackBar: this.snackBar,
        },
        panelClass: [snackbarClass],
      }
    );

    this.applyFocusOnSnackbar(snackbarRef, snackbarClass);
  }

  showBottomSnackBar(
    title: string,
    message: string,
    durationInSecond: number = 0,
    type: LoggerLevelType = LoggerLevelType.INFO
  ): void {
    if (
      levelLoggerCompare(
        type,
        this.configurationService.getConfig().loggerLevel
      )
    ) {
      const snackbarClass = 'snackbar-bottom-class';

      const snackbarRef = this.snackBar.openFromComponent(
        SnackbarBottomComponent,
        {
          duration: durationInSecond * 1000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          data: {
            title: title,
            message: message,
            currentSnackBar: this.snackBar,
            type: type,
          },
          panelClass: [snackbarClass],
        }
      );

      this.applyFocusOnSnackbar(snackbarRef, snackbarClass);
    }
  }

  isSnackBarAlreadyOpened() {
    return !!this.snackBar._openedSnackBarRef;
  }
}
